import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import BatchColumnLabel from './BatchColumnLabel';
import { formatNumber } from '../batchPushUtils';

export interface PushBatchRow {
    key: string;
    column: { wmsBatchNumber: string | null; displayNode: string | null; displayTime: string | null };
    count: number;
    areaNames: string[];
}

/** 区域列表：超过三行折叠（原型 clamp3），可展开看全部（PRD「查看已选明细」） */
const AreaList: React.FC<{ names: string[] }> = ({ names }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);
    const text = `${t('wms.batchPush.detail.areaCount', { count: names.length })}${names.join('、')}`;
    // 按实际渲染判断是否超过两行（区域名长短不一，不能按个数估算）
    useLayoutEffect(() => {
        const el = textRef.current;
        if (el && !expanded) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [text, expanded]);
    return (
        <div>
            <div ref={textRef} className={expanded ? '' : 'line-clamp-3'} title={names.join('、')}>{text}</div>
            {(overflowing || expanded) && (
                <Button type="link" size="small" className="px-0" onClick={() => setExpanded(!expanded)}>
                    {expanded ? t('wms.batchPush.detail.collapse') : t('wms.batchPush.detail.expand')}
                </Button>
            )}
        </div>
    );
};

/** 表头 / 单元格样式（对齐原型：表头 12.5px 半粗灰字，单元格 8px×10px，行底分隔线） */
const TH = 'font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap';
const TD = 'border-b border-gray-200 align-middle';
const CELL_PADDING: React.CSSProperties = { padding: '8px 10px' };
/** 批次列固定宽度：不再与推送区域列平分剩余宽度，批次与推送件数两列间距收紧（设计稿） */
const BATCH_COL_WIDTH = 200;
const COUNT_COL_WIDTH = 90;

/**
 * 推送批次表：批次、推送件数、推送区域。「查看已选明细」面板与「确认推送」弹框共用。
 */
const PushBatchTable: React.FC<{ rows: PushBatchRow[] }> = ({ rows }) => {
    const { t } = useTranslation();
    return (
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
                <tr className="text-left">
                    <th className={TH} style={{ ...CELL_PADDING, fontSize: 12.5, width: BATCH_COL_WIDTH }}>{t('wms.batchPush.detail.columns.batch')}</th>
                    <th className={`${TH} text-right`} style={{ ...CELL_PADDING, fontSize: 12.5, width: COUNT_COL_WIDTH }}>{t('wms.batchPush.detail.columns.count')}</th>
                    <th className={TH} style={{ ...CELL_PADDING, fontSize: 12.5 }}>{t('wms.batchPush.detail.columns.areas')}</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row.key}>
                        {/* 批次、推送区域字体加粗（设计稿）；推送件数保持粗体 */}
                        <td className={TD} style={CELL_PADDING}><BatchColumnLabel column={row.column} titleClassName="font-semibold" /></td>
                        <td className={`${TD} text-right font-bold tabular-nums`} style={CELL_PADDING}>{formatNumber(row.count)}</td>
                        <td className={`${TD} font-semibold`} style={CELL_PADDING}><AreaList names={row.areaNames} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default PushBatchTable;
